import React, { useState, useEffect } from "react";
import { PresetApiContent } from "@/types/page";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, AlertCircle, Grid, RefreshCw } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { he } from "date-fns/locale";
import FontSizeSelector, { FontSizeOption } from '@/components/ui/FontSizeSelector';
import { supabase } from '@/integrations/supabase/client';

interface PresetApiEditorProps {
  content: PresetApiContent;
  onContentChange: (field: string, value: any) => void;
}

const PRESET_API_ENDPOINTS = {
  "halacha-yomit": "https://wwosfhatkzqsgzhkigpx.supabase.co/functions/v1/halacha-yomit",
  "hayom-yom": "https://wwosfhatkzqsgzhkigpx.supabase.co/functions/v1/hayom-yom"
};

const API_TITLES = {
  "halacha-yomit": "הלכה יומית",
  "hayom-yom": "היום יום"
};

const PresetApiEditor: React.FC<PresetApiEditorProps> = ({
  content,
  onContentChange,
}) => {
  // Define available presets
  const presets = [
    { value: "halacha-yomit", label: "הלכה יומית" },
    { value: "hayom-yom", label: "היום יום" },
  ];

  // Text size options
  const textSizes = [
    { value: 'normal', label: 'רגיל' },
    { value: 'large', label: 'גדול' },
    { value: 'x-large', label: 'גדול מאוד' },
    { value: 'xx-large', label: 'ענק' },
  ];
  const fontSize: FontSizeOption = content.fontSize || 'md';
  const handleFontSizeChange = (value: FontSizeOption) => {
    onContentChange('fontSize', value);
  };

  // State for preview data
  const [previewData, setPreviewData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch preview data when preset type changes
  useEffect(() => {
    const fetchPreviewData = async () => {
      if (!content.presetType) {
        // No preset selected
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Get the API endpoint for the selected preset
        const endpoint = PRESET_API_ENDPOINTS[content.presetType];
        if (!endpoint) {
          throw new Error(`Unknown preset type: ${content.presetType}`);
        }

        // Get today's date in ISO format for some APIs that need it
        const today = new Date();
        const dateParam = format(today, 'yyyy-MM-dd');

        // Construct the API URL with any needed parameters
        let apiUrl = endpoint;
        if (content.presetType === "halacha-yomit") {
          apiUrl = `${endpoint}?date=${dateParam}`;
        } else if (content.presetType === "hayom-yom") {
          // For hayom yom from Chabad.org, we don't need any date parameters
          apiUrl = endpoint;
        }

        // Get the current auth token for the request
        const { data: sessionData } = await supabase.auth.getSession();
        const authToken = sessionData.session?.access_token || '';

        const response = await fetch(apiUrl, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }

        // Check if the response is JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Invalid response format: Expected JSON but received ' + contentType);
        }

        const data = await response.json();
        setPreviewData(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch API data";
        setError(errorMessage);
        console.error("Error fetching preset API preview:", err);
        setPreviewData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPreviewData();
  }, [content.presetType]);

  // Calculate text size class based on settings
  const getTextSizeClass = () => {
    switch (content.textSize) {
      case 'large': return 'text-xl';
      case 'x-large': return 'text-2xl';
      case 'xx-large': return 'text-3xl';
      default: return 'text-lg';
    }
  };

  // Handle manual refresh of preview
  const handleRefreshPreview = async () => {
    if (!content.presetType) {
      setError("No preset type selected");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get the API endpoint for the selected preset
      const endpoint = PRESET_API_ENDPOINTS[content.presetType];
      if (!endpoint) {
        throw new Error(`Unknown preset type: ${content.presetType}`);
      }

      // Get today's date in ISO format for some APIs that need it
      const today = new Date();
      const dateParam = format(today, 'yyyy-MM-dd');

      // Construct the API URL with any needed parameters
      let apiUrl = endpoint;
      if (content.presetType === "halacha-yomit") {
        apiUrl = `${endpoint}?date=${dateParam}`;
      } else if (content.presetType === "hayom-yom") {
        // For hayom yom from Chabad.org, we don't need any date parameters
        apiUrl = endpoint;
      }

      // Get the current auth token for the request
      const { data: sessionData } = await supabase.auth.getSession();
      const authToken = sessionData.session?.access_token || '';

      const response = await fetch(apiUrl, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      // Check if the response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Invalid response format: Expected JSON but received ' + contentType);
      }

      const data = await response.json();
      setPreviewData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch API data";
      setError(errorMessage);
      console.error("Error fetching preset API preview:", err);
      setPreviewData(null);
    } finally {
      setLoading(false);
    }
  };

  // Format the API data for preview display
  const renderPreviewContent = () => {
    if (!previewData) return null;

    if (content.presetType === "halacha-yomit") {
      // Halacha Yomit format from Sefaria
      return (
        <div className={`prose max-w-none ${getTextSizeClass()} max-h-96 overflow-y-auto p-4`}>
          {previewData.halachot && previewData.halachot.map((halacha: any, index: number) => (
            <div key={index} className="mb-6">
              <h3 className="font-bold text-center mb-3">{halacha.title}</h3>
              <div dangerouslySetInnerHTML={{ __html: halacha.text }} />
              {halacha.source && (
                <div className="text-right mt-2 text-gray-600">מקור: {halacha.source}</div>
              )}
            </div>
          ))}
        </div>
      );
    } else if (content.presetType === "hayom-yom") {
      // Hayom Yom format from Supabase Edge Function
      return (
        <div className={`prose max-w-none ${getTextSizeClass()} max-h-96 overflow-y-auto p-4`}>
          <div className="mb-6">
            <h3 className="font-bold text-center mb-3">{previewData.title}</h3>
            <div className="text-center mb-4 text-gray-600">
              {previewData.hebrewDate}
            </div>
            {previewData.shiurim && previewData.shiurim.length > 0 && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-bold mb-2">שיעורים:</h4>
                <ul className="list-none p-0 m-0">
                  {previewData.shiurim.map((shiur: string, index: number) => (
                    <li key={index} className="mb-1">{shiur}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-4 text-right" dir="rtl">
              {previewData.content}
            </div>
            <div className="text-right mt-4 text-gray-600">
              <a 
                href={previewData.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline"
              >
                מקור: {previewData.source}
              </a>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Title for the content
  const displayTitle = content.customTitle || API_TITLES[content.presetType] || content.title || "תוכן API";

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>הגדרות API</CardTitle>
          <CardDescription>
            בחר מקור תוכן מוגדר מראש ואת אופן הצגתו
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Preset Type Selection */}
          <div className="space-y-2">
            <Label htmlFor="presetType">סוג התוכן</Label>
            <Select
              value={content.presetType || ""}
              onValueChange={(value) => onContentChange("presetType", value)}
            >
              <SelectTrigger id="presetType">
                <SelectValue placeholder="בחר סוג תוכן" />
              </SelectTrigger>
              <SelectContent>
                {presets.map((preset) => (
                  <SelectItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom Title */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="customTitle">כותרת מותאמת אישית</Label>
              <div className="flex items-center space-x-2">
                <Label htmlFor="showTitle">הצג כותרת</Label>
                <Switch
                  id="showTitle"
                  checked={content.showTitle !== false}
                  onCheckedChange={(checked) =>
                    onContentChange("showTitle", checked)
                  }
                />
              </div>
            </div>
            <Input
              id="customTitle"
              value={content.customTitle || ""}
              onChange={(e) => onContentChange("customTitle", e.target.value)}
              placeholder="השאר ריק לשימוש בכותרת ברירת המחדל"
            />
          </div>

          {/* Show Date Toggle */}
          <div className="flex items-center space-x-2">
            <Label htmlFor="showDate">הצג תאריך</Label>
            <Switch
              id="showDate"
              checked={content.showDate !== false}
              onCheckedChange={(checked) =>
                onContentChange("showDate", checked)
              }
            />
          </div>

          {/* Text Size */}
          <div className="space-y-2">
            <Label htmlFor="textSize">גודל טקסט</Label>
            <Select
              value={content.textSize || "normal"}
              onValueChange={(value) => onContentChange("textSize", value)}
            >
              <SelectTrigger id="textSize">
                <SelectValue placeholder="בחר גודל טקסט" />
              </SelectTrigger>
              <SelectContent>
                {textSizes.map((size) => (
                  <SelectItem key={size.value} value={size.value}>
                    {size.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Font Size */}
          <FontSizeSelector value={fontSize} onChange={handleFontSizeChange} label="גודל גופן לתוכן" />

          {/* Refresh Interval */}
          <div className="space-y-2">
            <Label htmlFor="refreshInterval">רענון נתונים (בדקות)</Label>
            <Input
              id="refreshInterval"
              type="number"
              min="1"
              step="1"
              value={content.refreshInterval || 60}
              onChange={(e) =>
                onContentChange("refreshInterval", parseInt(e.target.value, 10))
              }
              placeholder="60"
            />
            <p className="text-sm text-gray-500">
              המערכת תרענן את הנתונים אוטומטית לפי הזמן שנקבע
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Grid className="h-5 w-5" />
            <span>תצוגה מקדימה</span>
          </CardTitle>
          <CardDescription>
            תצוגה מקדימה של התוכן כפי שיוצג בדף
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!content.presetType ? (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
              <p className="text-center text-gray-600">
                בחר סוג תוכן כדי לראות תצוגה מקדימה
              </p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-md">
              {/* Title and date (if enabled) */}
              {content.showTitle !== false && (
                <h2 className="text-2xl font-bold text-center p-4 pb-0">{displayTitle}</h2>
              )}
              {content.showDate !== false && (
                <div className="text-center text-gray-600 p-4 pt-2">
                  {format(new Date(), "dd MMMM yyyy", { locale: he })}
                </div>
              )}
              
              {/* Content preview */}
              {loading ? (
                <div className="flex justify-center items-center h-40 p-4">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : error ? (
                <div className="bg-red-50 text-red-800 p-4 rounded-md">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-5 w-5" />
                    <h3 className="font-bold">שגיאה בטעינת הנתונים</h3>
                  </div>
                  <p>{error}</p>
                </div>
              ) : (
                renderPreviewContent()
              )}
            </div>
          )}
        </CardContent>
        <CardFooter>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefreshPreview} 
            disabled={!content.presetType || loading}
            className="ml-auto flex items-center gap-1"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>רענן תצוגה מקדימה</span>
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default PresetApiEditor; 